// client/src/components/transportadores/TransportadorForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTransportadorForm } from '../../hooks/useTransportadores';
import { XCircle, Plus, Trash2, CheckCircle2, Loader2, UserCircle, Car } from 'lucide-react';
import useAlert from '../../hooks/useAlert';
import { lookupCep } from '../../utils/cepService';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const STEP_LABELS = ['Dados Gerais', 'Endereço', 'Motoristas', 'Veículos'];

const VEICULO_TIPOS = ['Moto', 'Carro', 'Caminhão', 'Van', 'Pick-up', 'Outro'];

const initialState = {
    tipoPessoa: 'Física',
    nomeCompleto: '',
    cpf: '',
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
    nomeRepresentante: '',
    cpfRepresentante: '',
    email: '',
    telefone: '',
    endereco: {
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        localidade: '',
        uf: '',
        pontoReferencia: ''
    },
    motoristas: [],
    veiculos: []
};

const motoristaInicial = { nomeCompleto: '', cpf: '', cnhNumero: '', cnhCategoria: '', cnhValidade: '' };
const veiculoInicial = { tipo: 'Carro', marca: '', modelo: '', ano: '', placa: '', rntrc: '' };

const formatCPF = (value) => {
    const v = (value || '').replace(/\D/g, '').slice(0, 11);
    return v.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const formatCNPJ = (value) => {
    const v = (value || '').replace(/\D/g, '').slice(0, 14);
    return v
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const TransportadorForm = ({ current, onCloseForm, onSaveSuccess }) => {
    const isEditing = !!current?._id;
    const { salvarTransportador, isLoading } = useTransportadorForm(current?._id);
    const { setAlert } = useAlert();

    const [transportador, setTransportador] = useState(initialState);
    const [currentStep, setCurrentStep] = useState(1);
    const [motorista, setMotorista] = useState(motoristaInicial);
    const [veiculo, setVeiculo] = useState(veiculoInicial);
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [cepEncontrado, setCepEncontrado] = useState(false);

    useEffect(() => {
        if (current) {
            setTransportador({
                ...initialState,
                ...current,
                endereco: { ...initialState.endereco, ...current.endereco },
                motoristas: current.motoristas || [],
                veiculos: current.veiculos || []
            });
        } else {
            setTransportador(initialState);
        }
        setCurrentStep(1);
    }, [current]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setTransportador(prev => ({ ...prev, [name]: value }));
    };

    const onDocumentoChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        const { name } = e.target;
        setTransportador(prev => ({ ...prev, [name]: raw }));
    };

    const onEnderecoChange = (e) => {
        const { name, value } = e.target;
        if (name === 'cep') setCepEncontrado(false);
        setTransportador(prev => ({ ...prev, endereco: { ...prev.endereco, [name]: value } }));
    };

    const handleCepBlur = useCallback(async (e) => {
        const raw = e.target.value.replace(/\D/g, '');
        if (raw.length !== 8) return;
        setIsCepLoading(true);
        try {
            const addr = await lookupCep(raw);
            setTransportador(prev => ({
                ...prev,
                endereco: {
                    ...prev.endereco,
                    logradouro: addr.logradouro || prev.endereco.logradouro,
                    bairro: addr.bairro || prev.endereco.bairro,
                    localidade: addr.localidade || prev.endereco.localidade,
                    uf: addr.uf || prev.endereco.uf,
                }
            }));
            setCepEncontrado(true);
            setAlert('CEP encontrado com sucesso!', 'success');
        } catch (err) {
            setAlert(err.message || 'Não foi possível consultar o CEP.', 'danger');
        } finally {
            setIsCepLoading(false);
        }
    }, [setAlert]);

    const handleAddMotorista = () => {
        if (!motorista.nomeCompleto || !motorista.cpf || !motorista.cnhNumero) {
            setAlert('Preencha nome, CPF e número da CNH do motorista.', 'warning');
            return;
        }
        setTransportador(prev => ({ ...prev, motoristas: [...prev.motoristas, motorista] }));
        setMotorista(motoristaInicial);
    };

    const handleRemoveMotorista = (index) => {
        setTransportador(prev => ({ ...prev, motoristas: prev.motoristas.filter((_, i) => i !== index) }));
    };

    const handleAddVeiculo = () => {
        if (!veiculo.tipo || !veiculo.placa) {
            setAlert('Preencha o tipo e a placa do veículo.', 'warning');
            return;
        }
        setTransportador(prev => ({ ...prev, veiculos: [...prev.veiculos, { ...veiculo, placa: veiculo.placa.toUpperCase() }] }));
        setVeiculo(veiculoInicial);
    };

    const handleRemoveVeiculo = (index) => {
        setTransportador(prev => ({ ...prev, veiculos: prev.veiculos.filter((_, i) => i !== index) }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (transportador.tipoPessoa === 'Física' && (!transportador.nomeCompleto || !transportador.cpf)) {
            setAlert('Nome completo e CPF são obrigatórios para pessoa física.', 'warning');
            return;
        }
        if (transportador.tipoPessoa === 'Jurídica' && (!transportador.razaoSocial || !transportador.cnpj)) {
            setAlert('Razão social e CNPJ são obrigatórios para pessoa jurídica.', 'warning');
            return;
        }
        if (!transportador.email) {
            setAlert('O email é obrigatório.', 'warning');
            return;
        }

        const payload = { ...transportador };
        if (payload.tipoPessoa === 'Física') {
            payload.razaoSocial = undefined;
            payload.nomeFantasia = undefined;
            payload.cnpj = undefined;
        } else {
            payload.nomeCompleto = undefined;
            payload.cpf = undefined;
        }

        try {
            await salvarTransportador(payload);
            onSaveSuccess ? onSaveSuccess() : onCloseForm();
        } catch (error) {
            console.error('Erro ao salvar transportador:', error);
        }
    };

    return (
        <form onSubmit={onSubmit} noValidate>
            <div className="form-header">
                <h3 className="form-title">{isEditing ? 'Editar Transportador' : 'Novo Transportador'}</h3>
                <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button">
                    <XCircle size={24} />
                </button>
            </div>

            <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

            {currentStep === 1 && (
                <fieldset className="form-section">
                    <legend className="form-section-title">Dados Gerais</legend>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label htmlFor="tipoPessoa">Tipo de Pessoa</label>
                            <select id="tipoPessoa" name="tipoPessoa" value={transportador.tipoPessoa} onChange={onChange} className="form-input">
                                <option value="Física">Física</option>
                                <option value="Jurídica">Jurídica</option>
                            </select>
                        </div>

                        {transportador.tipoPessoa === 'Física' ? (
                            <>
                                <div className="form-group">
                                    <label htmlFor="nomeCompleto">Nome Completo *</label>
                                    <input type="text" id="nomeCompleto" name="nomeCompleto" value={transportador.nomeCompleto} onChange={onChange} required className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="cpf">CPF *</label>
                                    <input type="text" id="cpf" name="cpf" value={formatCPF(transportador.cpf)} onChange={onDocumentoChange} required maxLength={14} className="form-input" />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="form-group">
                                    <label htmlFor="razaoSocial">Razão Social *</label>
                                    <input type="text" id="razaoSocial" name="razaoSocial" value={transportador.razaoSocial} onChange={onChange} required className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="nomeFantasia">Nome Fantasia</label>
                                    <input type="text" id="nomeFantasia" name="nomeFantasia" value={transportador.nomeFantasia} onChange={onChange} className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="cnpj">CNPJ *</label>
                                    <input type="text" id="cnpj" name="cnpj" value={formatCNPJ(transportador.cnpj)} onChange={onDocumentoChange} required maxLength={18} className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="nomeRepresentante">Nome do Representante</label>
                                    <input type="text" id="nomeRepresentante" name="nomeRepresentante" value={transportador.nomeRepresentante} onChange={onChange} className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="cpfRepresentante">CPF do Representante</label>
                                    <input type="text" id="cpfRepresentante" name="cpfRepresentante" value={formatCPF(transportador.cpfRepresentante)} onChange={onDocumentoChange} maxLength={14} className="form-input" />
                                </div>
                            </>
                        )}

                        <div className="form-group">
                            <label htmlFor="email">Email *</label>
                            <input type="email" id="email" name="email" value={transportador.email} onChange={onChange} required className="form-input" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="telefone">Telefone</label>
                            <input type="text" id="telefone" name="telefone" value={transportador.telefone} onChange={onChange} className="form-input" />
                        </div>
                    </div>
                </fieldset>
            )}

            {currentStep === 2 && (
                <fieldset className="form-section">
                    <legend className="form-section-title">Endereço</legend>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label htmlFor="cep">CEP</label>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    id="cep"
                                    name="cep"
                                    value={transportador.endereco.cep}
                                    onChange={onEnderecoChange}
                                    onBlur={handleCepBlur}
                                    disabled={isCepLoading}
                                    className="form-input"
                                    placeholder="00000-000"
                                    maxLength={9}
                                    style={{ paddingRight: isCepLoading ? '2.25rem' : undefined }}
                                />
                                {isCepLoading && (
                                    <Loader2 size={16} className="animate-spin" style={{ position: 'absolute', right: '0.75rem', color: '#64748b', pointerEvents: 'none' }} />
                                )}
                            </div>
                            {cepEncontrado ? (
                                <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                    <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                                    Endereço preenchido automaticamente.
                                    <button type="button" onClick={() => setCepEncontrado(false)}
                                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                                        Editar manualmente
                                    </button>
                                </div>
                            ) : (
                                <div className="form-hint">Digite o CEP e aguarde o preenchimento automático</div>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="logradouro">Logradouro</label>
                            <input
                                type="text" id="logradouro" name="logradouro" value={transportador.endereco.logradouro} onChange={onEnderecoChange}
                                className="form-input" readOnly={cepEncontrado}
                                style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="numero">Número</label>
                            <input type="text" id="numero" name="numero" value={transportador.endereco.numero} onChange={onEnderecoChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="complemento">Complemento</label>
                            <input type="text" id="complemento" name="complemento" value={transportador.endereco.complemento} onChange={onEnderecoChange} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label htmlFor="bairro">Bairro</label>
                            <input
                                type="text" id="bairro" name="bairro" value={transportador.endereco.bairro} onChange={onEnderecoChange}
                                className="form-input" readOnly={cepEncontrado}
                                style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="localidade">Cidade</label>
                            <input
                                type="text" id="localidade" name="localidade" value={transportador.endereco.localidade} onChange={onEnderecoChange}
                                className="form-input" readOnly={cepEncontrado}
                                style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="uf">Estado (UF)</label>
                            <input
                                type="text" id="uf" name="uf" value={transportador.endereco.uf} onChange={onEnderecoChange}
                                className="form-input" maxLength="2" readOnly={cepEncontrado}
                                style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="pontoReferencia">Ponto de Referência</label>
                            <input type="text" id="pontoReferencia" name="pontoReferencia" value={transportador.endereco.pontoReferencia} onChange={onEnderecoChange} className="form-input" />
                        </div>
                    </div>
                </fieldset>
            )}

            {currentStep === 3 && (
                <fieldset className="form-section">
                    <legend className="form-section-title">Motoristas</legend>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label>Nome Completo</label>
                            <input type="text" value={motorista.nomeCompleto} onChange={(e) => setMotorista(prev => ({ ...prev, nomeCompleto: e.target.value }))} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>CPF</label>
                            <input type="text" value={formatCPF(motorista.cpf)} onChange={(e) => setMotorista(prev => ({ ...prev, cpf: e.target.value.replace(/\D/g, '') }))} maxLength={14} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Número da CNH</label>
                            <input type="text" value={motorista.cnhNumero} onChange={(e) => setMotorista(prev => ({ ...prev, cnhNumero: e.target.value }))} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Categoria da CNH</label>
                            <input type="text" value={motorista.cnhCategoria} onChange={(e) => setMotorista(prev => ({ ...prev, cnhCategoria: e.target.value }))} placeholder="Ex.: B, D, E" className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Validade da CNH</label>
                            <input type="date" value={motorista.cnhValidade} onChange={(e) => setMotorista(prev => ({ ...prev, cnhValidade: e.target.value }))} className="form-input" />
                        </div>
                    </div>
                    <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '0.75rem' }}>
                        <button type="button" onClick={handleAddMotorista} className="action-button action-button-secondary">
                            <Plus size={18} className="icon" /> Adicionar Motorista
                        </button>
                    </div>

                    {transportador.motoristas.length > 0 && (
                        <div className="table-wrapper" style={{ marginTop: '1rem' }}>
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>CPF</th>
                                        <th>CNH</th>
                                        <th>Categoria</th>
                                        <th>Validade</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transportador.motoristas.map((m, i) => (
                                        <tr key={i}>
                                            <td className="table-cell"><UserCircle size={15} className="inline mr-1.5 text-gray-500" />{m.nomeCompleto}</td>
                                            <td className="table-cell">{formatCPF(m.cpf)}</td>
                                            <td className="table-cell">{m.cnhNumero}</td>
                                            <td className="table-cell">{m.cnhCategoria || '—'}</td>
                                            <td className="table-cell">{m.cnhValidade ? new Date(m.cnhValidade).toLocaleDateString('pt-BR') : '—'}</td>
                                            <td className="text-right">
                                                <button type="button" onClick={() => handleRemoveMotorista(i)} title="Remover" className="action-button-icon text-red-600 hover:text-red-900">
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </fieldset>
            )}

            {currentStep === 4 && (
                <fieldset className="form-section">
                    <legend className="form-section-title">Veículos</legend>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label>Tipo</label>
                            <select value={veiculo.tipo} onChange={(e) => setVeiculo(prev => ({ ...prev, tipo: e.target.value }))} className="form-input">
                                {VEICULO_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Placa</label>
                            <input type="text" value={veiculo.placa.toUpperCase()} onChange={(e) => setVeiculo(prev => ({ ...prev, placa: e.target.value }))} maxLength={8} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Marca</label>
                            <input type="text" value={veiculo.marca} onChange={(e) => setVeiculo(prev => ({ ...prev, marca: e.target.value }))} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Modelo</label>
                            <input type="text" value={veiculo.modelo} onChange={(e) => setVeiculo(prev => ({ ...prev, modelo: e.target.value }))} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Ano</label>
                            <input type="text" value={veiculo.ano} onChange={(e) => setVeiculo(prev => ({ ...prev, ano: e.target.value }))} maxLength={4} className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>RNTRC</label>
                            <input type="text" value={veiculo.rntrc} onChange={(e) => setVeiculo(prev => ({ ...prev, rntrc: e.target.value }))} className="form-input" />
                        </div>
                    </div>
                    <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '0.75rem' }}>
                        <button type="button" onClick={handleAddVeiculo} className="action-button action-button-secondary">
                            <Plus size={18} className="icon" /> Adicionar Veículo
                        </button>
                    </div>

                    {transportador.veiculos.length > 0 && (
                        <div className="table-wrapper" style={{ marginTop: '1rem' }}>
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Tipo</th>
                                        <th>Placa</th>
                                        <th>Marca/Modelo</th>
                                        <th>Ano</th>
                                        <th>RNTRC</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transportador.veiculos.map((v, i) => (
                                        <tr key={i}>
                                            <td className="table-cell"><Car size={15} className="inline mr-1.5 text-gray-500" />{v.tipo}</td>
                                            <td className="table-cell">{v.placa}</td>
                                            <td className="table-cell">{[v.marca, v.modelo].filter(Boolean).join(' ') || '—'}</td>
                                            <td className="table-cell">{v.ano || '—'}</td>
                                            <td className="table-cell">{v.rntrc || '—'}</td>
                                            <td className="text-right">
                                                <button type="button" onClick={() => handleRemoveVeiculo(i)} title="Remover" className="action-button-icon text-red-600 hover:text-red-900">
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </fieldset>
            )}

            <div className="form-actions">
                <button type="button" onClick={onCloseForm} className="action-button action-button-secondary" disabled={isLoading}>
                    <XCircle size={18} className="icon" />Cancelar
                </button>
                <FormStepNavigation
                    currentStep={currentStep}
                    totalSteps={STEP_LABELS.length}
                    onBack={() => setCurrentStep((s) => s - 1)}
                    onNext={() => setCurrentStep((s) => s + 1)}
                    isSubmitting={isLoading}
                    submitLabel={isEditing ? 'Salvar Alterações' : 'Criar Transportador'}
                />
            </div>
        </form>
    );
};

export default TransportadorForm;
